import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User,
  CreditCard,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  Building2,
  ArrowLeft,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { companyService } from '@/lib/companyService';
import { useAuth } from '@/features/auth';
import { useCompany } from '@/context/CompanyContext';
import { emitToast } from '@/components/ui/Toast';
import { INVITE_TOKEN_KEY } from './InviteLandingPage';

/* ─────────────────────────────────────────────────────────────────────────────
   JoinPage
   Route: /join/:inviteToken   (PUBLIC – no auth required to render)

   Business Logic
   ──────────────
   1.  STATUS = 'onboarding'  (pre-approved via invite)
   2.  DUPLICATE PREVENTION — idempotent; re-clicking the link is safe.
   3.  ATOMIC dual-write via the `join_company_via_invite` SECURITY DEFINER
       RPC.  The RPC writes BOTH:
         • hr_employees   (role = 'onboarding')
         • company_members (role = 'member')   ← RBAC Level 3
       in a single DB transaction, bypassing RLS for the member insert only
       (the user is not yet a member when they first call this).
   4.  REDIRECT → /employees/:id?tab=personal  (Onboarding Step 1)

   Auth gate
   ─────────
   If the user is NOT logged in, token is saved to sessionStorage and the
   user is bounced to /signup?invite=<token>.  After auth, the app navigates
   back here with the user set, and the form renders.
────────────────────────────────────────────────────────────────────────────── */

/* ── Israeli ID (Luhn-style) validator ── */
function isValidIsraeliId(id: string): boolean {
  const cleaned = id.trim();
  if (!/^\d{9}$/.test(cleaned)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(cleaned[i]!, 10) * (i % 2 === 0 ? 1 : 2);
    if (digit > 9) digit -= 9;
    sum += digit;
  }
  return sum % 10 === 0;
}

/* ── Reusable labelled field ── */
function Field({
  id,
  label,
  icon,
  hint,
  children,
}: {
  id: string;
  label: string;
  icon: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
        {icon}
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main component
────────────────────────────────────────────────────────────────────────────── */
export function JoinPage() {
  const { inviteToken = '' } = useParams<{ inviteToken: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // ── Global workspace context — refresh + auto-select after successful join.
  const { refreshCompanies, setActiveCompanyId } = useCompany();

  /* ── Company resolution (name shown in UI; id not needed — RPC takes token) */
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyError,   setCompanyError]   = useState<string | null>(null);
  const [companyName,    setCompanyName]    = useState<string>('');

  /* ── Form state ── */
  const [firstName,  setFirstName]  = useState('');
  const [lastName,   setLastName]   = useState('');
  const [nationalId, setNationalId] = useState('');

  /* ── Submission state ── */
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState<string | null>(null);
  const [success,      setSuccess]      = useState(false);
  const [alreadyJoined, setAlreadyJoined] = useState(false);

  /* ── Validation errors ── */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /* ─────────────────────────────────────────────────────────────────────────
     Helper: redirect employee to Step 1 (personal details tab)
  ───────────────────────────────────────────────────────────────────────── */
  function goToOnboardingStep1(employeeId: string) {
    sessionStorage.removeItem(INVITE_TOKEN_KEY);
    navigate(`/employees/${employeeId}?tab=personal`, { replace: true });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Auth gate + company resolution
     • Not logged in  → save token, bounce to /signup
     • Logged in      → resolve company from token
  ───────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (authLoading) return;

    if (!inviteToken) {
      setCompanyError('קישור ההצטרפות אינו תקין. אנא פנה למנהל שלך.');
      setCompanyLoading(false);
      return;
    }

    if (!user) {
      // Not authenticated → save token and send to sign-up
      sessionStorage.setItem(INVITE_TOKEN_KEY, inviteToken);
      navigate(`/signup?invite=${inviteToken}`, { replace: true });
      return;
    }

    // Authenticated → resolve company
    (async () => {
      const { data, error } = await companyService.getCompanyByInviteToken(inviteToken);
      setCompanyLoading(false);
      if (error || !data) {
        setCompanyError(error ?? 'קישור לא תקין');
      } else {
        setCompanyName(data.legal_name);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken, user, authLoading]);

  /* ── Client-side validation ── */
  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!firstName.trim()) errors.firstName = 'שם פרטי הוא שדה חובה';
    if (!lastName.trim())  errors.lastName  = 'שם משפחה הוא שדה חובה';

    const nidClean = nationalId.replace(/\D/g, '');
    if (!nidClean) {
      errors.nationalId = 'תעודת זהות היא שדה חובה';
    } else if (nidClean.length !== 9) {
      errors.nationalId = 'תעודת זהות חייבת להכיל 9 ספרות';
    } else if (!isValidIsraeliId(nidClean)) {
      errors.nationalId = 'מספר תעודת הזהות אינו תקין';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  /* ─────────────────────────────────────────────────────────────────────────
     refreshWorkspaceContext
     Forces CompanyContext to re-fetch the user’s company list from the DB
     and auto-selects the company they just joined.  This makes the Topbar
     CompanySelector and role-aware Sidebar light up immediately without
     requiring a full page reload.
  ───────────────────────────────────────────────────────────────────────── */
  async function refreshWorkspaceContext(companyId: string) {
    await refreshCompanies();                           // re-fetch company list
    if (companyId) setActiveCompanyId(companyId);      // point selector at new company
  }

  /* ─────────────────────────────────────────────────────────────────────────
     redirectExistingEmployee
     ──────────────────────────────────────────────────────────────────────────
     Called when either:
       a) The RPC returns already_member = true  (graceful idempotent path), or
       b) Postgres throws 23505 (unique violation on national_id + company).

     In case (b) the RPC did NOT return an employee_id, so we do a quick
     SELECT on hr_employees to find the existing record before redirecting.
     We also ensure the company_members row exists (idempotent INSERT).
  ───────────────────────────────────────────────────────────────────────── */
  async function redirectExistingEmployee(
    knownEmployeeId: string | null,
    companyId: string
  ) {
    let empId = knownEmployeeId;

    if (!empId) {
      // Fetch the existing hr_employees row for this user + company
      const { data: empRows } = await supabase
        .from('hr_employees')
        .select('id')
        .eq('company_id', companyId)
        .eq('user_id', user!.id)
        .limit(1)
        .single();

      empId = empRows?.id ?? null;
    }

    // Refresh global context so Topbar selector + Sidebar role-routing react.
    if (companyId) await refreshWorkspaceContext(companyId);

    emitToast(
      'אתה כבר רשום כעובד בחברה זו. מעביר אותך למערכת…',
      'info'
    );
    setAlreadyJoined(true);
    setSuccess(true);

    setTimeout(() => {
      if (empId) {
        goToOnboardingStep1(empId);
      } else {
        // Rare: employee row missing — fall back to dashboard
        sessionStorage.removeItem(INVITE_TOKEN_KEY);
        navigate('/app', { replace: true });
      }
    }, 1500);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Submit — calls the `join_company_via_invite` SECURITY DEFINER RPC.

     The RPC atomically:
       ① Verifies the invite token.
       ② Inserts hr_employees  (onboarding_status = 'onboarding').
       ③ Inserts company_members (role = 'member')  ← RBAC Level 3.
       ④ Returns { employee_id, company_name, already_member }.

     Both inserts use ON CONFLICT DO NOTHING so the call is idempotent.

     Error handling matrix:
       already_member = true → redirect (no error shown)
       23505 (unique violation on national_id) → resolve + redirect (no error)
       invalid_invite_token  → blocking error message
       anything else         → blocking error message
  ───────────────────────────────────────────────────────────────────────── */
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    if (!user || !inviteToken) {
      setSubmitError('שגיאת מערכת. אנא רענן את הדף ונסה שוב.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const nidClean = nationalId.replace(/\D/g, '');

    /* ── Call the atomic SECURITY DEFINER RPC ── */
    const { data: rows, error: rpcErr } = await supabase.rpc(
      'join_company_via_invite',
      {
        p_token:       inviteToken,
        p_first_name:  firstName.trim(),
        p_last_name:   lastName.trim(),
        p_national_id: nidClean,
      }
    );

    setSubmitting(false);

    /* ── RPC returned an error ─────────────────────────────────────────────
       23505 = unique_violation (national_id already registered in company).
       This means the national ID belongs to someone already in the system —
       most likely THIS same user re-clicking the link with the same ID.
       Instead of blocking them, we look up their existing record and redirect.
    ───────────────────────────────────────────────────────────────────────── */
    if (rpcErr) {
      console.error('[JoinPage] join_company_via_invite RPC error:', rpcErr);

      if (rpcErr.code === '23505') {
        // Resolve the company_id from the token (already in state as companyName,
        // but we need the id — re-fetch it silently using the service).
        const { data: company } = await companyService.getCompanyByInviteToken(inviteToken);
        const companyId = company?.id ?? '';
        await redirectExistingEmployee(null, companyId);
        return;
      }

      if (rpcErr.message?.includes('invalid_invite_token')) {
        setSubmitError('קישור ההזמנה אינו תקין או שפג תוקפו. אנא פנה למנהל שלך.');
      } else {
        setSubmitError(rpcErr.message ?? 'שגיאה בלתי צפויה. אנא נסה שוב.');
      }
      return;
    }

    /* ── RPC succeeded ─────────────────────────────────────────────────────
       The RPC returns a single row via RETURNS TABLE.
    ───────────────────────────────────────────────────────────────────────── */
    const result        = Array.isArray(rows) ? rows[0] : rows;
    const employeeId    = result?.employee_id   as string | undefined;
    const alreadyMember = result?.already_member as boolean | undefined;

    if (!employeeId) {
      setSubmitError('שגיאה בלתי צפויה. אנא נסה שוב.');
      return;
    }

    if (alreadyMember) {
      // already_member flag from RPC — graceful idempotent path
      const { data: company } = await companyService.getCompanyByInviteToken(inviteToken);
      await redirectExistingEmployee(employeeId, company?.id ?? '');
      return;
    }

    /* ── Fresh join: refresh context so Topbar/Sidebar light up immediately ── */
    const { data: joinedCompany } = await companyService.getCompanyByInviteToken(inviteToken);
    await refreshWorkspaceContext(joinedCompany?.id ?? '');

    emitToast('ברוך הבא! מעביר אותך לשלב הראשון…', 'success');
    setSuccess(true);
    setTimeout(() => goToOnboardingStep1(employeeId), 1500);
  }

  /* ─── Auth resolving spinner ─── */
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">מאמת זהות…</p>
        </div>
      </div>
    );
  }

  /* ─── Company resolving spinner ─── */
  if (companyLoading && user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">טוען פרטי החברה…</p>
        </div>
      </div>
    );
  }

  /* ─── Invalid / expired token ─── */
  if (companyError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="rtl">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-red-100 p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <AlertCircle className="w-7 h-7 text-red-400" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">קישור לא תקין</h2>
          <p className="text-sm text-red-600">{companyError}</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium
                       hover:bg-gray-200 transition-colors"
          >
            חזרה לדף הכניסה
          </button>
        </div>
      </div>
    );
  }

  /* ─── Success splash (shown briefly before redirect fires) ─── */
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-green-50
                      flex items-center justify-center p-4" dir="rtl">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-green-100
                        p-8 text-center space-y-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto
                          ${alreadyJoined ? 'bg-blue-50' : 'bg-green-50'}`}>
            <CheckCircle2 className={`w-8 h-8 ${alreadyJoined ? 'text-blue-500' : 'text-green-500'}`} />
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            {alreadyJoined ? 'כבר רשום במערכת!' : 'הצטרפת בהצלחה!'}
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            {alreadyJoined
              ? 'אתה כבר רשום כעובד בחברה זו. מעביר אותך למערכת…'
              : 'מעביר אותך לשלב הראשון של תהליך הקליטה…'}
          </p>
          <Loader2 className="w-5 h-5 animate-spin text-primary-500 mx-auto" />
        </div>
      </div>
    );
  }

  /* ─── Main form ─── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50
                    flex items-start justify-center p-4 pt-10 sm:items-center" dir="rtl">
      <div className="w-full max-w-md space-y-6">

        {/* ── Header ── */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl
                          bg-primary-100 text-primary-600 mb-1">
            <Building2 className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">הצטרפות לצוות</h1>
          {companyName && (
            <div className="inline-flex items-center gap-1.5 bg-white border border-gray-200
                            rounded-full px-4 py-1.5 text-sm font-medium text-gray-700 shadow-sm">
              <Building2 className="w-3.5 h-3.5 text-primary-500" />
              {companyName}
            </div>
          )}
          <p className="text-sm text-gray-500 leading-relaxed max-w-xs mx-auto pt-1">
            מלא את הפרטים האישיים שלך כדי להתחיל בתהליך הקליטה.
          </p>
        </div>

        {/* ── Form card ── */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

          {/* Security banner */}
          <div className="bg-primary-50 border-b border-primary-100 px-5 py-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary-500 shrink-0" />
            <p className="text-xs text-primary-700 font-medium">
              הפרטים ישמרו בצורה מאובטחת ולא יועברו לצד שלישי
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="px-5 py-6 space-y-5">

            {/* First Name */}
            <Field
              id="firstName"
              label="שם פרטי"
              icon={<User className="w-3.5 h-3.5 text-gray-400" />}
            >
              <input
                id="firstName"
                type="text"
                autoComplete="given-name"
                autoFocus
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setFieldErrors((p) => ({ ...p, firstName: '' }));
                }}
                placeholder="ישראל"
                className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900
                           placeholder-gray-400 bg-gray-50
                           focus:outline-none focus:ring-2 focus:ring-primary-400
                           focus:border-transparent transition
                           ${fieldErrors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              />
              {fieldErrors.firstName && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{fieldErrors.firstName}
                </p>
              )}
            </Field>

            {/* Last Name */}
            <Field
              id="lastName"
              label="שם משפחה"
              icon={<User className="w-3.5 h-3.5 text-gray-400" />}
            >
              <input
                id="lastName"
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setFieldErrors((p) => ({ ...p, lastName: '' }));
                }}
                placeholder="ישראלי"
                className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900
                           placeholder-gray-400 bg-gray-50
                           focus:outline-none focus:ring-2 focus:ring-primary-400
                           focus:border-transparent transition
                           ${fieldErrors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              />
              {fieldErrors.lastName && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{fieldErrors.lastName}
                </p>
              )}
            </Field>

            {/* National ID */}
            <Field
              id="nationalId"
              label="תעודת זהות"
              icon={<CreditCard className="w-3.5 h-3.5 text-gray-400" />}
              hint="9 ספרות – תשמש כמזהה ייחודי וקבוע של תיק העובד שלך"
            >
              <input
                id="nationalId"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={9}
                value={nationalId}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setNationalId(digits);
                  setFieldErrors((p) => ({ ...p, nationalId: '' }));
                }}
                placeholder="000000000"
                className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900
                           placeholder-gray-400 bg-gray-50 font-mono tracking-widest
                           focus:outline-none focus:ring-2 focus:ring-primary-400
                           focus:border-transparent transition
                           ${fieldErrors.nationalId ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                dir="ltr"
              />
              {fieldErrors.nationalId && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />{fieldErrors.nationalId}
                </p>
              )}
            </Field>

            {/* Global submit error */}
            {submitError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-100
                              text-red-700 rounded-xl px-4 py-3 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2
                         bg-primary-600 hover:bg-primary-700 active:bg-primary-800
                         disabled:bg-primary-300 disabled:cursor-not-allowed
                         text-white font-bold text-sm
                         px-4 py-4 rounded-xl
                         transition-colors min-h-[52px]"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" />שומר פרטים…</>
              ) : (
                <><ArrowLeft className="w-4 h-4" />התחל תהליך קליטה</>
              )}
            </button>
          </form>
        </div>

        {/* Security footer */}
        <p className="text-center text-xs text-gray-400 flex items-center justify-center gap-1.5 pb-6">
          <ShieldCheck className="w-3.5 h-3.5" />
          מוגן בהצפנת SSL 256-bit
        </p>
      </div>
    </div>
  );
}
